import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Compass, Users, Coins, Path, Timer, ShieldWarning, ChatCircleDots, ArrowRight, ArrowLeft, X,
} from "@phosphor-icons/react";
import { useBeta } from "../context/BetaContext";

const STEPS = [
  {
    icon: Compass,
    title: "Welcome to the Aurumward private beta",
    body: "You're one of the first explorers to test Aurumward. Everything you play with is virtual Gold — it has no cash value and can never be bought, sold, or withdrawn. Thank you for helping us shape the journey.",
  },
  {
    icon: Users,
    title: "Choose and develop your crew",
    body: "Every expedition, you pick three crew members. They gain experience, loyalty, and stress over time — and can be injured. Match their strengths to the expedition, then rest and heal them at the Recovery Camp between runs.",
  },
  {
    icon: Coins,
    title: "Review the stake clearly",
    body: "Before an expedition begins you confirm a stake of virtual Gold. The screen shows your balance, the stake limits, and your estimated odds. You can never lose more than the stake you confirm.",
  },
  {
    icon: Path,
    title: "Make five strategic decisions",
    body: "Each expedition presents five branching decisions. Every option shows an advantage and a risk. Your choices — together with your crew and skill — shape the outcome.",
  },
  {
    icon: Timer,
    title: "Understand the timing challenge",
    body: "One skill challenge asks you to stop at the right moment. It's validated on the server and contributes to your final score, so a steady hand pays off.",
  },
  {
    icon: ShieldWarning,
    title: "Threat is descriptive only",
    body: "The Threat meter reflects how risky your situation is becoming. It's there to inform you — it never secretly changes your score or the outcome. A limited, disclosed chance factor is always part of the result.",
  },
  {
    icon: ChatCircleDots,
    title: "Tell us when something's off",
    body: "If anything feels confusing or broken, tap Feedback (in the top bar or Settings) and let us know. Your notes go straight to the team and directly influence what we fix next.",
  },
];

export default function OnboardingWalkthrough() {
  const { onboarding, updateOnboarding } = useBeta();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const shouldShow = !!(onboarding && onboarding.eligible && !onboarding.seen);

  // Resume at the last incomplete step when the walkthrough (re)opens.
  useEffect(() => {
    if (shouldShow) {
      const last = Math.min(Math.max(onboarding.last_step || 0, 0), STEPS.length - 1);
      setStep(last);
    }
  }, [shouldShow, onboarding]);

  if (!shouldShow) return null;

  const isLast = step === STEPS.length - 1;
  const Icon = STEPS[step].icon;

  const persistStep = (n) => updateOnboarding({ last_step: n }).catch(() => {});

  const next = async () => {
    if (busy) return;
    if (isLast) {
      setBusy(true);
      try {
        await updateOnboarding({ completed: true, last_step: STEPS.length - 1 });
      } finally {
        setBusy(false);
      }
      return;
    }
    const n = step + 1;
    setStep(n);
    persistStep(n);
  };

  const back = () => {
    if (step === 0) return;
    const n = step - 1;
    setStep(n);
    persistStep(n);
  };

  const skip = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await updateOnboarding({ skipped: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" data-testid="onboarding-walkthrough">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35 }}
        className="ff-panel relative w-full max-w-md rounded-md p-6 sm:p-8"
      >
        <button
          onClick={skip}
          data-testid="onboarding-skip-btn"
          className="absolute right-3 top-3 flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-ff-muted transition-colors hover:text-ff-gold"
        >
          Skip <X size={14} />
        </button>

        <div className="mb-4 flex items-center gap-1.5" data-testid="onboarding-progress">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                i < step ? "bg-ff-gold" : i === step ? "bg-ff-brass" : "bg-ff-brass/20"
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.25 }}
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-ff-gold/40 bg-ff-gold/10">
              <Icon size={30} weight="duotone" className="text-ff-gold" />
            </div>
            <p className="text-center font-mono-ff text-[10px] uppercase tracking-widest text-ff-brass">
              Step {step + 1} of {STEPS.length}
            </p>
            <h2 className="mt-1 text-center font-display text-2xl font-bold leading-snug text-ff-parchment" data-testid="onboarding-title">
              {STEPS[step].title}
            </h2>
            <p className="mt-3 text-center text-sm leading-relaxed text-ff-muted" data-testid="onboarding-body">
              {STEPS[step].body}
            </p>
          </motion.div>
        </AnimatePresence>

        <div className="mt-6 flex items-center gap-2">
          {step > 0 && (
            <button
              onClick={back}
              data-testid="onboarding-back-btn"
              className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-4 py-2.5 text-sm text-ff-muted transition-colors hover:text-ff-gold"
            >
              <ArrowLeft size={16} /> Back
            </button>
          )}
          <button
            onClick={next}
            disabled={busy}
            data-testid="onboarding-next-btn"
            className="flex flex-1 items-center justify-center gap-1.5 rounded-sm bg-ff-gold py-2.5 font-semibold text-ff-bg transition-all hover:bg-ff-brass active:scale-95 disabled:opacity-60"
          >
            {isLast ? "Start exploring" : "Next"} <ArrowRight size={16} />
          </button>
        </div>
      </motion.div>
    </div>
  );
}
